/**
 * Error Handler Utility
 *
 * Centralized error handling for API errors.
 * Transforms backend errors into user-friendly messages with appropriate titles.
 *
 * @example
 * try {
 *   await apiClient.post('/endpoint', data);
 * } catch (error) {
 *   const { title, message } = handleApiError(error);
 *   showError(message, title);
 * }
 */

import axios from 'axios';
import { ApiErrorResponse } from '../types/api';

/**
 * Error information to display to the user
 */
export interface ErrorInfo {
  title: string;
  message: string;
  statusCode?: number;
}

/**
 * Handle API errors and return user-friendly error information
 *
 * @param error - The error object (typically from axios or apiClient)
 * @returns Object containing title and message for display
 *
 * @example
 * try {
 *   await apiClient.post('/endpoint', data);
 * } catch (error) {
 *   const { title, message } = handleApiError(error);
 *   showError(message, title);
 * }
 */
export function handleApiError(error: any): ErrorInfo {
  // Handle network/connection errors
  if (!axios.isAxiosError(error) || !error.response) {
    return {
      title: 'Errore di Connessione',
      message: 'Impossibile connettersi al server. Verifica la tua connessione.',
      statusCode: undefined
    };
  }

  const status = error.response.status;
  const errorData: ApiErrorResponse = error.response.data || {};
  const errorMessage = errorData.error || errorData.message || '';
  const errorDetail = errorData.detail || '';

  console.error(`API Error ${status}`);

  // Build error info based on status code
  const errorInfo = getErrorInfoByStatus(status, errorMessage, errorDetail, errorData);

  return {
    ...errorInfo,
    statusCode: status
  };
}

/**
 * Get error info based on HTTP status code
 * @internal
 */
function getErrorInfoByStatus(
  status: number,
  errorMessage: string,
  errorDetail: string,
  errorData: ApiErrorResponse
): Omit<ErrorInfo, 'statusCode'> {
  switch (status) {
    // Validation errors
    case 422:
      return {
        title: 'Dati Non Validi',
        message: 'Dati mancanti o non validi. Verifica i campi inseriti.'
      };

    // Bad request errors
    case 400:
      return handle400Error(errorMessage, errorDetail);

    // Authentication errors
    case 401:
      return {
        title: 'Autenticazione Fallita',
        message: 'Chiave di autenticazione non valida. Riprova ad accedere.'
      };

    // Authorization errors
    case 403:
      return handle403Error(errorData);

    // Not found errors
    case 404:
      return handle404Error(errorData);

    // Service unavailable
    case 503:
      return {
        title: 'Servizio Non Disponibile',
        message: 'Il servizio è temporaneamente non disponibile. Riprova tra qualche minuto.'
      };

    // Internal server errors
    case 500:
      return handle500Error(errorDetail);

    // Default case
    default:
      return {
        title: 'Errore',
        message: errorDetail || errorMessage || 'Si è verificato un errore imprevisto.'
      };
  }
}

/**
 * Handle 400 Bad Request errors
 * @internal
 */
function handle400Error(errorMessage: string, errorDetail: string): Omit<ErrorInfo, 'statusCode'> {
  // Check for specific error patterns
  if (errorDetail?.includes('must contain between')) {
    return {
      title: 'Errore Validazione',
      message: 'Il numero di coppie deve essere tra 1 e 50.'
    };
  }

  if (errorMessage.toLowerCase().includes('password')) {
    return {
      title: 'File Protetto da Password',
      message: 'Uno o più file sono protetti da password. Rimuovi la protezione e riprova.'
    };
  }

  if (errorMessage.includes('pwc_api_key') || errorMessage.includes('pwc_identity_token')) {
    return {
      title: 'Autenticazione Mancante',
      message: 'Autenticazione mancante. Riprova ad accedere.'
    };
  }

  return {
    title: 'Richiesta Non Valida',
    message: errorDetail || errorMessage || 'La richiesta contiene dati non validi.'
  };
}

/**
 * Handle 403 Forbidden errors
 * @internal
 */
function handle403Error(errorData: ApiErrorResponse): Omit<ErrorInfo, 'statusCode'> {
  if (errorData.error === 'FORBIDDEN') {
    return {
      title: 'Accesso Negato',
      message: 'Non hai i permessi per accedere a questa risorsa.'
    };
  }

  return {
    title: 'Permessi Insufficienti',
    message: 'Permessi insufficienti per accedere alla risorsa.'
  };
}

/**
 * Handle 404 Not Found errors
 * @internal
 */
function handle404Error(errorData: ApiErrorResponse): Omit<ErrorInfo, 'statusCode'> {
  if (errorData.error === 'JOB_NOT_FOUND') {
    return {
      title: 'Job Non Trovato',
      message: 'Il job richiesto non esiste o è stato eliminato.'
    };
  }

  return {
    title: 'Risorsa Non Trovata',
    message: 'La risorsa richiesta non è stata trovata. Verifica i parametri.'
  };
}

/**
 * Handle 500 Internal Server Error
 * @internal
 */
function handle500Error(errorDetail: string): Omit<ErrorInfo, 'statusCode'> {
  // Parse specific error details
  if (typeof errorDetail === 'string') {
    if (errorDetail.includes('convert PDF to text')) {
      return {
        title: 'Errore di Conversione',
        message: 'Impossibile convertire il PDF in testo. Verifica che il file non sia corrotto.'
      };
    }

    if (errorDetail.includes('Invalid PDF format') || errorDetail.includes('corrupted')) {
      return {
        title: 'PDF Non Valido',
        message: 'Il file PDF è corrotto o non valido.'
      };
    }

    if (errorDetail.includes('OCR')) {
      return {
        title: 'Errore OCR',
        message: 'Errore durante il riconoscimento ottico del testo.'
      };
    }

    if (errorDetail.includes('similarity')) {
      return {
        title: 'Errore Calcolo Similarità',
        message: 'Impossibile calcolare la similarità tra i documenti.'
      };
    }

    if (errorDetail.includes('signed URL')) {
      return {
        title: 'Errore Accesso File',
        message: 'Impossibile accedere al file.'
      };
    }
  }

  return {
    title: 'Errore Interno',
    message: 'Si è verificato un errore durante l\'elaborazione. Riprova.'
  };
}

/**
 * Determine the appropriate toast type based on status code
 *
 * @param statusCode - HTTP status code
 * @returns 'error', 'warning', or 'info'
 *
 * @example
 * const errorInfo = handleApiError(error);
 * const toastType = getToastTypeForError(errorInfo.statusCode);
 * if (toastType === 'warning') {
 *   showWarning(errorInfo.message, errorInfo.title);
 * } else {
 *   showError(errorInfo.message, errorInfo.title);
 * }
 */
export function getToastTypeForError(statusCode?: number): 'error' | 'warning' | 'info' {
  if (!statusCode) return 'error';

  // User-actionable errors (warnings)
  if (statusCode === 400 || statusCode === 422 || statusCode === 404) {
    return 'warning';
  }

  // Service unavailable (info)
  if (statusCode === 503) {
    return 'info';
  }

  // All other errors
  return 'error';
}
